import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowUpRight, Search } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { certificates, type Category, type Certificate } from "@/data/certificates";
import { certificatePathFor } from "@/data/certificateSlugs";
import { getBookingAction } from "@/data/certificateBookingActions";
import { cn } from "@/lib/utils";
import { LazyBookingDialog } from "@/components/site/LazyBookingDialog";
import { CategoryBadge } from "@/components/site/CategoryBadge";
import { ModeBadges } from "@/components/site/ModeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const categoryOrder: Category[] = [
  "korkort", "alkohol", "sjofolk", "arbete", "resor", "forsakring", "sport", "adoption", "afs", "ovriga",
];

export const Services = ({ hideHeader = false }: { hideHeader?: boolean } = {}) => {
  const { t, lang } = useLang();
  const [filter, setFilter] = useState<Category | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [bookDialogOpen, setBookDialogOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const list = certificates.filter((c) => {
    if (filter !== "all" && c.category !== filter) return false;
    if (!normalizedQuery) return true;
    const haystack = [c.title[lang], c.description[lang], t.categories[c.category]]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  });
  const selectedAction = selectedCert ? getBookingAction(selectedCert.slug) : null;

  return (
    <section
      id="certifikat"
      className={cn(
        "bg-background",
        hideHeader ? "py-8 lg:py-10" : "py-20 lg:py-28",
      )}
    >
      <div className="container-wide">
        {!hideHeader ? (
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">{t.services.eyebrow}</span>
              <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {t.services.title}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">{t.services.subtitle}</p>
            </div>
          </div>
        ) : (
          <div className="flex min-h-28 items-center justify-center">
            <h2 className="text-center font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              {t.services.allTitle}
            </h2>
          </div>
        )}

        <div className="group relative mt-6 flex items-center gap-2 rounded-full border border-border/70 bg-white p-1.5 shadow-[0_8px_30px_-12px_hsl(162_52%_8%/0.28)] ring-1 ring-black/[0.03] transition-base focus-within:border-primary/30 focus-within:shadow-[0_12px_36px_-12px_hsl(162_52%_8%/0.35)] focus-within:ring-primary/15">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground transition-base group-focus-within:text-primary"
              aria-hidden
            />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.services.searchPlaceholder}
              aria-label={t.services.searchAriaLabel}
              className="h-12 w-full border-0 bg-transparent pl-12 pr-10 text-base shadow-none placeholder:text-muted-foreground/80 focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-base hover:bg-muted hover:text-foreground"
                aria-label={lang === "sv" ? "Rensa sökning" : "Clear search"}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")}>
            {t.services.filterAll}
          </FilterPill>
          {categoryOrder.map((c) => (
            <FilterPill key={c} active={filter === c} onClick={() => setFilter(c)}>
              {t.categories[c]}
            </FilterPill>
          ))}
        </div>

        {list.length === 0 ? (
          <p className="mt-10 text-center text-sm text-muted-foreground">{t.services.noResults}</p>
        ) : (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((cert) => (
              <article
                key={cert.slug}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card p-6 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated"
              >
                <div className="flex items-center justify-between">
                  <CategoryBadge category={cert.category} link />
                  <ModeBadges modes={cert.modes} />
                </div>

                <h3 className="mt-5 font-display text-xl font-semibold leading-tight text-foreground">
                  {cert.title[lang]}
                </h3>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                  {cert.description[lang]}
                </p>

                <div className="mt-5 flex items-end justify-between border-t border-border/60 pt-4">
                  <div>
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Pris</span>
                    <div className="font-display text-lg font-semibold text-foreground">{cert.price[lang]}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button asChild variant="ghost-outline" size="sm">
                      <a
                        href={certificatePathFor(cert.slug, lang)}
                        aria-label={t.services.readMoreAbout.replace("{title}", cert.title[lang])}
                      >
                        {t.services.readMore}
                      </a>
                    </Button>
                    <Button
                      variant="hero"
                      size="sm"
                      onClick={() => {
                        setSelectedCert(cert);
                        setBookDialogOpen(true);
                      }}
                    >
                      {t.services.bookCta}
                      <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <p className="mt-10 text-center text-sm text-muted-foreground">{t.services.empty}</p>
        <LazyBookingDialog
          open={bookDialogOpen}
          onOpenChange={(open) => {
            setBookDialogOpen(open);
            if (!open) setSelectedCert(null);
          }}
          title={selectedCert?.title[lang] ?? t.services.bookCta}
          action={selectedAction}
          contentKey={selectedCert?.slug}
        />
      </div>
    </section>
  );
};

const FilterPill = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "rounded-full border px-4 py-2 text-sm font-medium transition-base",
      active
        ? "border-primary bg-primary text-primary-foreground shadow-card"
        : "border-border bg-card text-foreground/80 hover:border-primary/40 hover:text-primary"
    )}
  >
    {children}
  </button>
);
